/**
 * Full-page Evidence Upload — replaces the prior CaseDetail.js dialog.
 * Mounted at /cases/:caseId/evidence/new.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Upload, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/canonical';

const TOKEN_KEY = 'nexus_token';

export default function EvidenceUploadPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);

  const [caseInfo, setCaseInfo] = useState(null);
  const [evidenceType, setEvidenceType] = useState('DOCUMENT');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceDesc, setEvidenceDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setCaseInfo)
      .catch(() => {});
  }, [caseId, token]);

  const goBack = () => navigate(`/cases/${caseId}?tab=evidence`);

  const handleUpload = async () => {
    if (!evidenceFile) {
      toast.error('Please select a file to upload');
      return;
    }
    setProgress(0);
    setUploading(true);
    try {
      await new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', evidenceFile);
        fd.append('description', evidenceDesc);
        fd.append('evidence_type', evidenceType);
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else {
            try { reject(new Error(JSON.parse(xhr.responseText).detail || `Upload failed (${xhr.status})`)); }
            catch { reject(new Error(`Upload failed (${xhr.status})`)); }
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        xhr.open('POST', `${BACKEND_URL}/api/cases/${caseId}/evidence`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(fd);
      });
      toast.success('Evidence uploaded successfully');
      goBack();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="evidence-upload-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="evidence-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-amber-300" /> Upload Evidence
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-3xl">
        <div>
          <Label className="text-slate-300">Evidence Type</Label>
          <Select value={evidenceType} onValueChange={setEvidenceType}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="evidence-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['PHOTO','VIDEO','AUDIO','DOCUMENT','PDF','OTHER'].map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">File *</Label>
          <Input
            type="file"
            onChange={e => setEvidenceFile(e.target.files[0])}
            className="bg-slate-800 border-slate-600 text-white file:text-slate-200"
            data-testid="evidence-file-input"
          />
          {evidenceFile && (
            <p className="text-[11px] text-slate-400 mt-1">
              Selected: <span className="text-slate-200 font-mono">{evidenceFile.name}</span> ({(evidenceFile.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>
        <div>
          <Label className="text-slate-300">Description</Label>
          <Textarea
            value={evidenceDesc}
            onChange={e => setEvidenceDesc(e.target.value)}
            className="bg-slate-800 border-slate-600 text-white"
            rows={4}
            placeholder="Describe the evidence, its source, chain of custody, and relevance to the case…"
            data-testid="evidence-desc-input"
          />
        </div>

        {uploading && (
          <div className="space-y-1" data-testid="evidence-upload-progress">
            <div className="flex justify-between text-xs text-slate-300">
              <span>Uploading…</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-amber-500 transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="evidence-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="evidence-cancel">
          Cancel
        </Button>
        <Button onClick={handleUpload} disabled={uploading || !evidenceFile}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="submit-evidence-btn">
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading… {progress}%</>
          ) : (
            <><Upload className="w-4 h-4 mr-1.5" /> Upload Evidence</>
          )}
        </Button>
      </div>
    </div>
  );
}
